import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import LogTimeline from './LogTimeline';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { LogTimelineEventProps } from './LogTimelineEvent';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { MEDIA_BREAKPOINTS } from '../../design-system/settings/variables/Breakpoints';
import {
  IPLookup,
  getIPInfo,
  ipLookup,
  parseIPInfo,
} from 'admin/pages/SESLogs/utils';
import { useApiRequest } from '../../auth/hooks/useApiRequest';
import {
  getSESLogInfo,
  messageInfoProperties,
  userInfoProperties,
} from 'admin/pages/SESLogs/eventUtils';
import { thinScrollbarStyles } from '../../design-system/settings/variables/Scrollbar';

const StyledDiv = styled.div`
  ${thinScrollbarStyles}
  position: relative;
  height: 100%;
  overflow: auto;
  background-color: ${COLORS.whiteBase};
  padding: ${SPACERS.medium};
  border-radius: 4px;
  border: 1px solid ${COLORS.greyLight};
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
  .content-wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: ${SPACERS.extraLarge};
    .text-wrapper {
      width: 100%;
      padding: ${SPACERS.tiny};
      row-gap: 1px;
      display: grid;
      grid-template-columns: fit-content(25px) 1fr;
      grid-template-areas:
        'icon name'
        'icon time';
      div:nth-child(1) {
        grid-area: icon;
      }
      div:nth-child(2) {
        grid-area: name;
      }
      div:nth-child(3) {
        grid-area: time;
      }
    }
    ${MEDIA_BREAKPOINTS.largerThanMedium} {
      grid-template-columns: 1fr 3fr;
    }
  }
  .info-wrapper {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.medium};
    margin-right: ${SPACERS.large};
    .title {
      margin-bottom: ${SPACERS.small};
    }
  }
  .info {
    background-color: ${COLORS.greyLightest};
    border: 1px solid ${COLORS.greyLight};
    border-radius: 4px;
    padding: ${SPACERS.medium};
  }
  .item-wrapper {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.tiny};
  }
`;

interface LogModalProps {
  timelineData: LogTimelineEventProps[];
}

const LogModal: React.FC<LogModalProps> = ({ timelineData }) => {
  const [request, makeRequest] = useApiRequest<IPLookup>();

  const [eventInfo, setEvenInfo] = useState<{
    index: number;
    ip: string | undefined;
    body: string | undefined;
    userInfo: {
      info: React.ReactNode;
      label: React.ReactNode;
    }[];
    messageInfo: {
      info: React.ReactNode;
      label: React.ReactNode;
    }[];
  }>({
    index: 0,
    ip:
      timelineData[0].originalEvent.openIpAddress ||
      timelineData[0].originalEvent.clickIpAddress,
    body: timelineData[0].originalEvent.body,
    userInfo: getSESLogInfo(timelineData[0].originalEvent, userInfoProperties),
    messageInfo: getSESLogInfo(
      timelineData[0].originalEvent,
      messageInfoProperties,
    ),
  });

  const handleClick = useCallback(
    (event: LogTimelineEventProps, index: number) => {
      const ip =
        event.originalEvent.openIpAddress || event.originalEvent.clickIpAddress;
      ip && makeRequest(ipLookup(ip));
      setEvenInfo({
        index,
        ip,
        body: event.originalEvent.body,
        userInfo: getSESLogInfo(event.originalEvent, userInfoProperties),
        messageInfo: getSESLogInfo(event.originalEvent, messageInfoProperties),
      });
    },
    [setEvenInfo],
  );

  useEffect(() => {
    if (request.type !== 'REQUEST_SUCCESS') return;
    const ipInfo = getIPInfo(request);
    if (!ipInfo) return;

    setEvenInfo(s => {
      const newState = { ...s };
      newState.userInfo = newState.userInfo.concat(parseIPInfo(ipInfo));
      return newState;
    });
  }, [request, setEvenInfo]);

  return (
    <StyledDiv>
      <div className="content-wrapper">
        <LogTimeline
          selectedIndex={eventInfo.index}
          timelineEvents={timelineData}
          handleClick={handleClick}
        />
        <div className="info-wrapper">
          <div className="info">
            <TrueImpactText variant="body-large-bold" className="title">
              User Info
            </TrueImpactText>
            <div className="item-wrapper">
              {eventInfo.userInfo.map(item => (
                <TrueImpactText variant="body-small" className="info-item">
                  <strong>{item.label}:</strong> {item.info}
                </TrueImpactText>
              ))}
            </div>
          </div>
          <div className="info">
            <TrueImpactText variant="body-large-bold" className="title">
              Message Info
            </TrueImpactText>
            <div className="item-wrapper">
              {eventInfo.messageInfo.map(item => (
                <TrueImpactText variant="body-small" className="info-item">
                  <strong>{item.label}:</strong> {item.info}
                </TrueImpactText>
              ))}
            </div>
          </div>
          <div className="info">
            <TrueImpactText variant="body-large-bold" className="title">
              Message Body
            </TrueImpactText>
            <div className="item-wrapper">
              <div
                dangerouslySetInnerHTML={{
                  __html: eventInfo.body || 'Unknown',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </StyledDiv>
  );
};

export default LogModal;
